<script lang="ts" setup>
import { dayjs } from 'element-plus'
import Card2 from '@/components/Card/Card2.vue'
import { type IEventCalendar } from '@/utils/types/Event'
import { dateFormat } from '@/utils/helper'
import { useNow } from '@vueuse/core'
import { useEventStore } from '@/stores'
const eventStore = useEventStore()

const props = defineProps({
  data: {
    type: Object as PropType<IEventCalendar>,
    default: {}
  }
})

const events = ref(props.data.list)

// 有活動的日期
const activeDays = ref(props.data.date ?? [])
const selectDateValue = ref(dateFormat(useNow(), 'YYYY-MM'))
const dataLoading: Ref<boolean> = ref(false)
const currentMonth: Ref<any> = ref(new Date(Date.parse(selectDateValue.value)))
const currentDate: Ref<string> = ref('')
const eventsFilter = computed(() =>
  currentDate.value === ''
    ? events.value
    : events.value.filter(o => o.started_date === currentDate.value)
)

const getSelectMonth = () => {
  let monthList = []
  const now = useNow()
  const startDate = '2023-08-01'
  const endDate = now.value
  // 多三個月
  endDate.setMonth(endDate.getMonth() + 3)
  const diffLength = monthsDiff(startDate, endDate.toISOString().split('T')[0])
  for (let i = 0; i < diffLength; i++) {
    const date = new Date(startDate)
    date.setMonth(date.getMonth() + i)
    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    monthList.push({
      value: `${year}-${month}`,
      label: `${year}-${month}月`,
    })
  }
  return monthList
}
const selectDateAry = getSelectMonth()

const dataLoadingView = () => {
  // dataLoading.value = true
  // setTimeout(() => {
  //   dataLoading.value = false
  // }, 1000)
}
const handleCalendarClick = (data: any) => {
  selectDateValue.value = dateFormat(data.day, 'YYYY-MM')
  currentDate.value = data.day
  dataLoadingView()
}

watch(
  () => selectDateValue.value,
  (v) => {
    currentMonth.value = new Date(Date.parse(v))
    currentDate.value = ''
    eventStore.fetchEventCalendar(v)
  }
)

watch(
  () => props.data,
  (v) => {
    events.value = v.list
    activeDays.value = v.date
    dataLoadingView()
  },
  {
    deep: true,
    immediate: true
  }
)

</script>

<template>
  <section class="zonezoneBox3">
    <div class="zonezoneBox3__left">
      <div class="zonezoneBox3__left__title" data-aos="fade-up" data-aos-duration="1500">
        events
      </div>
      <div class="zonezoneBox3__eventsBox">
        <div class="zonezoneBox3__eventsMove" :class="{ isLoading: dataLoading }"></div>
        <ul class="zonezoneBox3__events">
          <li v-for="event in eventsFilter" :key="event.id">
            <Card2 :item="event" />
          </li>
        </ul>
      </div>
    </div>
    <div class="zonezoneBox3__right">
      <div class="zonezoneBox3__right__top" data-aos="fade-up" data-aos-duration="1500">
        <div class="zonezoneBox3__right__top__enTitle">calendar</div>
        <div class="zonezoneBox3__right__top__titleBox">
          <div class="zonezoneBox3__right__top__twTitle">活動日曆</div>
          <div class="zonezoneBox3__right__top__text">活動報名中</div>
        </div>
      </div>
      <el-select v-model="selectDateValue" class="zonezone-select" placeholder="Select">
        <el-option v-for="item in selectDateAry" :key="item.value" :label="item.label" :value="item.value" />
      </el-select>
      <el-calendar class="zonezone-calendar" v-model="currentMonth">
        <template #date-cell="{ data }">
          <div class="zonezone-calendar__box" @click="handleCalendarClick(data)">
            <div class="zonezone-calendar__date"
              :class="{ isActive: activeDays.indexOf(dayjs(data.day).format('YYYY-MM-DD')) !== -1 }">
              {{ dayjs(data.day).format('D') }}
            </div>
          </div>
        </template>
      </el-calendar>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.zonezoneBox3 {
  margin-bottom: toRem(180);

  @include min-media(991) {
    @include grid;
  }

  @include max-media(990) {
    display: flex;
    flex-direction: column-reverse;
    margin-bottom: toRem(90);
  }

  &__left {
    @include min-media(991) {
      grid-column: 1 / span 4;
      margin-top: auto;
    }

    &__title {
      // font-size: toRem(80);
      font-size: 5.2vw;
      font-family: $font-TextaAlt;
      font-weight: 500;
      color: $color-white-1;

      @include max-media(640) {
        font-size: toRem(36);
      }
    }
  }

  &__right {
    @include min-media(991) {
      grid-column: 6 / span 7;
    }

    &__top {
      color: $color-white-1;
      margin-bottom: toRem(30);

      &__enTitle {
        // font-size: toRem(158);
        font-size: 10vw;
        font-weight: 500;
        font-family: $font-TextaAlt;
        letter-spacing: toRem(-3.16);
        line-height: 1;

        @include max-media(640) {
          font-size: toRem(80);
        }
      }

      &__titleBox {
        display: flex;
        align-items: flex-end;
      }

      &__twTitle {
        // font-size: toRem(48);
        font-size: 3.1vw;
        font-weight: 500;
        line-height: 1;
        margin-right: toRem(13);

        @include max-media(640) {
          font-size: toRem(28);
        }
      }

      &__text {
        position: relative;
        font-size: toRem(14);
        padding-left: toRem(18);

        &::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          margin-top: toRem(-4);
          width: toRem(8);
          height: toRem(8);
          background-color: $color-brand-1;
        }
      }
    }
  }

  &__eventsBox {
    position: relative;
    overflow: hidden;
  }

  &__eventsMove {
    position: absolute;
    left: 0;
    top: 0;
    z-index: 10;
    width: 100%;
    height: 250%;
    background-color: $color-black-1;
    border-radius: 500px;
    transform: translateY(100%);

    &.isLoading {
      animation: 1.8s eventsMove ease-in-out;
    }
  }

  &__events {
    max-height: toRem(580);
    overflow-y: auto;

    li {
      margin-bottom: toRem(24);
    }
  }
}

@keyframes eventsMove {
  0% {
    transform: translateY(100%);
  }

  100% {
    transform: translateY(-100%);
  }
}
</style>
<style lang="scss">
.zonezone-select {
  width: toRem(145);
  margin-bottom: toRem(10);

  .el-input {
    &__wrapper {
      height: 34px;
      background-color: transparent;
      border-radius: toRem(4);
      border: 1px solid $color-white-1;
      box-shadow: none !important;

      &.is-focus {
        box-shadow: none !important;
      }
    }

    &__inner {
      color: $color-white-1;
      font-size: toRem(12);
    }

    &__suffix {
      color: $color-white-1;
    }

    &.is-focus {
      .el-input {
        &__wrapper {
          box-shadow: none !important;
        }
      }
    }

    .el-select {
      &__caret {
        color: $color-white-1;
        font-size: toRem(16);
      }
    }
  }
}

.zonezone-calendar {
  background-color: transparent;

  .el-calendar {
    &__header {
      display: none;
      color: #fff;
    }

    &__body {
      padding: 0;
    }

    &-table {
      th {
        font-size: toRem(14);
        color: $color-white-1;

        &::before {
          content: '星期';

          @include max-media(990) {
            content: '';
          }
        }
      }

      tr {
        &:first-child {
          td {
            border-color: $color-gray-line;
          }
        }

        td {
          &:first-child {
            border-color: $color-gray-line;
          }
        }
      }

      td {
        border-color: $color-gray-line;

        &.prev,
        &.next {
          .zonezone-calendar {
            &__box {
              color: #7d7d7d;
            }
          }
        }

        &.is-selected {
          background-color: transparent;
        }
      }

      .el-calendar-day {
        display: flex;
        padding: 0;
        height: toRem(113);

        @include max-media(640) {
          height: toRem(80);
        }

        &:hover {
          background-color: rgba($color-white-1, 0.08);
        }

        .zonezone-calendar {
          &__box {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            color: $color-white-1;
          }

          &__date {
            position: relative;

            &.isActive {
              &::before {
                content: '';
                position: absolute;
                left: 50%;
                margin-left: toRem(-4);
                top: toRem(-8);
                width: toRem(8);
                height: toRem(8);
                background-color: $color-brand-1;
              }
            }
          }
        }
      }
    }
  }
}
</style>
